import AsyncStorage from '@react-native-async-storage/async-storage';
import type { SavedAddress } from './places';
import { NO_ADDRESS } from './places';

/**
 * What survives closing the app.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS BEFORE THERE IS A BACKEND
 * ---------------------------------------------------------------------------
 * Staying signed in is a client job, not a server one. A backend verifies the
 * OTP and hands back a token; it never remembers your device. If the app does
 * not store what it was given, a real backend changes nothing - you would sign
 * in again on every cold start, exactly as you do now.
 *
 * So the storage layer lands first. When auth becomes real the only change is
 * that `Session` carries a token instead of a flag; the read, the write and the
 * rehydrate on launch are already here.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT SECURE STORAGE
 * ---------------------------------------------------------------------------
 * AsyncStorage is unencrypted - on a rooted or jailbroken device it is a plain
 * file. That is fine for a stub flag, an address and a basket, none of which is
 * a credential. A real token does NOT belong here: move that one value to
 * `react-native-keychain` (iOS Keychain / Android Keystore) when it exists, and
 * leave everything else where it is.
 *
 * Every read is defensive. Stored JSON is data written by an older build of the
 * app, so it is treated like input: anything that fails to parse or does not
 * match the expected shape falls back to the default rather than propagating a
 * broken value into state.
 */

/** One namespace, so a stray key cannot collide with a library's. */
const KEY = {
  session: 'homingo.session',
  address: 'homingo.address',
  cart: 'homingo.cart',
  /**
   * The hours picked for each hourly service in the basket.
   *
   * A second key rather than a wider `cart` value, because `asCart`
   * has always dropped anything that is not a number - widening it would make
   * every basket already on a phone unreadable on the next launch. See
   * `data/hourly.ts` for why the two maps cannot drift in the direction
   * that would matter.
   */
  cartHours: 'homingo.cartHours',
  onboarded: 'homingo.onboarded',
  privacy: 'homingo.privacy',
  settings: 'homingo.settings',
  /**
   * Addresses the server refused because we do not serve that city yet.
   *
   * Its own key rather than a field inside `address`: that one holds the
   * single line currently on the header, while this is a list, and clearing
   * one must not clear the other.
   */
  pendingAddresses: 'homingo.pendingAddresses',
  /**
   * The pin behind the line in `address`.
   *
   * Its own key rather than widening `address` to an object: that key has
   * been a bare string on every install to date, and changing its shape would
   * make every one of them fall back to `NO_ADDRESS` on upgrade. A separate
   * key is simply absent on an old install, which the reader already treats
   * as "no pin known".
   */
  addressPin: 'homingo.addressPin',
} as const;

export type Session = {
  signedIn: boolean;
  /** Verified digits, or null when signed out. */
  phone: string | null;
  name: string | null;
  email: string | null;
  /**
   * The account's id on the server. Null on a session from before auth was
   * real, which is why every consumer treats it as optional.
   */
  userId?: string | null;
  /**
   * The bearer token, and the one that renews it.
   *
   * ---------------------------------------------------------------------------
   * THESE ARE CREDENTIALS, AND THIS FILE IS NOT SECURE STORAGE
   * ---------------------------------------------------------------------------
   * The warning at the top of this file applies to exactly these two fields
   * and to nothing else here. AsyncStorage is an unencrypted file: on a rooted
   * or jailbroken device, anyone who can read it can sign in as this customer
   * until the refresh token expires.
   *
   * They are here anyway because the alternative today is signing in on every
   * cold start, and `react-native-keychain` is a native dependency that needs
   * a rebuild. THE FIX IS SMALL AND SHOULD HAPPEN BEFORE RELEASE: move these
   * two values - and only these two - to the Keychain / Keystore, and leave
   * the rest of the session where it is.
   *
   * Two things already limit the damage: the access token expires in fifteen
   * minutes, and changing `JWT_SECRET` on the server invalidates every token
   * ever issued.
   */
  accessToken?: string | null;
  refreshToken?: string | null;
  /**
   * A local URI for the profile picture, or null.
   *
   * Only a path is kept, never the image itself. The picker copies what you
   * choose into the app's own cache and hands back a URI into it - so this
   * survives a restart, but not someone clearing the app's cache from system
   * settings. A missing file renders as the initial, which is the same fallback
   * as never having chosen one.
   */
  photo: string | null;
};

export const emptySession: Session = {
  signedIn: false,
  phone: null,
  name: null,
  email: null,
  photo: null,
  userId: null,
  accessToken: null,
  refreshToken: null,
};

/**
 * The privacy switches, as the account page sets them.
 *
 * Stored separately from the session on purpose: they are device preferences,
 * not identity, so signing out must not reset them.
 */
export type PrivacySettings = {
  /** Ask for the device lock before opening the app. */
  appLock: boolean;
  /** Share anonymous usage data. */
  analytics: boolean;
  /** Use booking history to pick which offers to show. */
  personalised: boolean;
  /** Let professionals see your live location while a job is running. */
  shareLive: boolean;
};

export const defaultPrivacy: PrivacySettings = {
  appLock: false,
  analytics: true,
  personalised: true,
  shareLive: true,
};

/**
 * App preferences, as the Settings page sets them.
 *
 * Device preferences like the privacy switches, and kept in their own key for
 * the same reason: signing out changes who you are, not what language you read
 * the app in.
 *
 * `theme` is stored as the three-way choice the user made, not as the colour
 * scheme that produced - 'system' has to stay 'system', or following the phone
 * would freeze to whatever it happened to be on the day it was set.
 */
export type AppSettings = {
  /** Language code. 'en' | 'hi' | 'mr' - the three the copy exists in. */
  language: string;
  /** ISO currency code. Prices are INR everywhere for now. */
  currency: string;
  /** 'system' follows the phone; the other two override it. */
  theme: 'system' | 'light' | 'dark';
  /** Sound and vibration on taps and confirmations. */
  sounds: boolean;
  /** Fetch images and offers on mobile data, not only on Wi-Fi. */
  dataSaver: boolean;
};

export const defaultSettings: AppSettings = {
  language: 'en',
  currency: 'INR',
  theme: 'system',
  sounds: true,
  dataSaver: false,
};

/** Everything the app needs before it can decide which screen to open on. */
export type RestoredApp = {
  seenOnboarding: boolean;
  /**
   * The one line currently in use, not the book - that lives on the platform
   * now, behind `useAddresses`. This survives a restart on its own because it
   * has to work before sign-in, during onboarding, when there is no account
   * yet for a book to belong to.
   */
  address: string;
  /**
   * Where that line actually is, when it is known.
   *
   * Undefined on an install that predates this key, and after a "Set your
   * location" reset. Coverage is the reason it is kept at all: the serviceable
   * check needs a pin, and until this existed the only way to get one was to
   * match the line against the address book - which is empty during
   * onboarding and for anyone signed out, so the check simply never ran and
   * the coming-soon banner never appeared.
   */
  addressPin?: { lat: number; lng: number };
  session: Session;
  cart: Record<string, number>;
  /** Hours per hourly service - see `KEY.cartHours`. */
  cartHours: Record<string, number>;
};

// ---------------------------------------------------------------- primitives

/**
 * Reads and parses one key.
 *
 * Swallows both a missing key and a failed parse, because neither is worth
 * failing a launch over: the caller's default is always a usable state.
 */
async function read<T>(key: string, fallback: T): Promise<T> {
  try {
    const raw = await AsyncStorage.getItem(key);
    if (raw === null) {
      return fallback;
    }
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * Writes one key, fire-and-forget.
 *
 * Nothing awaits these. A failed write means the value is not there next
 * launch, which the defensive reads already handle - blocking a tap on the
 * disk would be the worse trade.
 */
function write(key: string, value: unknown): void {
  AsyncStorage.setItem(key, JSON.stringify(value)).catch(() => {});
}

// ------------------------------------------------------------------ the API

/**
 * A pin, or undefined if what was stored is not one.
 *
 * Both numbers checked rather than assumed: this key is absent on every
 * install that predates it, and a half-written or hand-edited file must give
 * "no pin" rather than a NaN that a geocode would then be asked to resolve.
 */
const asPin = (value: unknown): { lat: number; lng: number } | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const { lat, lng } = value as { lat?: unknown; lng?: unknown };
  return Number.isFinite(lat) && Number.isFinite(lng)
    ? { lat: lat as number, lng: lng as number }
    : undefined;
};

/** A string, or the default if what was stored is not one. */
const asString = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.length > 0 ? value : fallback;

/** Quantities only. A malformed entry drops rather than poisoning the basket. */
function asCart(value: unknown): Record<string, number> {
  if (typeof value !== 'object' || value === null) {
    return {};
  }
  const out: Record<string, number> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, count]) => {
    if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
      out[key] = count;
    }
  });
  return out;
}

function asSession(value: unknown): Session {
  if (typeof value !== 'object' || value === null) {
    return emptySession;
  }
  const raw = value as Record<string, unknown>;
  /*
   * A session is only restored whole. Half of one - signed in with no number -
   * is worse than none, because the screens that read `phone` would render a
   * profile with a blank identity on it.
   */
  if (raw.signedIn !== true || typeof raw.phone !== 'string') {
    return emptySession;
  }
  /*
   * The credentials come back too, and forgetting them was the whole bug.
   *
   * `saveSession` has always written `userId`, `accessToken` and
   * `refreshToken` - this function simply did not copy them out again, so
   * every launch restored a session that said `signedIn: true` and carried no
   * way to prove it. The first authenticated request 401'd, the refresh
   * handler found no refresh token to trade, and the app signed the customer
   * out: "it doesn't save the login", one cold start at a time.
   *
   * `?? null` rather than dropping a bad value silently: these three are
   * optional on the type, and a session stored before auth was real has none
   * of them. That case must still restore - it is a signed-in phone number
   * with no server account behind it - so a missing token is null, not a
   * reason to throw the whole session away.
   */
  const str = (field: unknown): string | null =>
    typeof field === 'string' && field.length > 0 ? field : null;

  return {
    signedIn: true,
    phone: raw.phone,
    name: str(raw.name),
    email: str(raw.email),
    photo: str(raw.photo),
    userId: str(raw.userId),
    accessToken: str(raw.accessToken),
    refreshToken: str(raw.refreshToken),
  };
}

/**
 * Everything, in one round trip.
 *
 * `getMany` rather than four awaits: this runs while the splash is on screen
 * and each separate call would add its own bridge hop to the launch. Note the
 * v3 names - `multiGet`/`multiRemove` were renamed and no longer exist.
 */
export async function loadApp(): Promise<RestoredApp> {
  const fallback: RestoredApp = {
    seenOnboarding: false,
    address: NO_ADDRESS,
    session: emptySession,
    cart: {},
    cartHours: {},
  };

  try {
    const found = await AsyncStorage.getMany([
      KEY.onboarded,
      KEY.address,
      KEY.addressPin,
      KEY.session,
      KEY.cart,
      KEY.cartHours,
    ]);

    const parse = (key: string): unknown => {
      const raw = found[key];
      if (!raw) {
        return null;
      }
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    return {
      seenOnboarding: parse(KEY.onboarded) === true,
      address: asString(parse(KEY.address), NO_ADDRESS),
      addressPin: asPin(parse(KEY.addressPin)),
      session: asSession(parse(KEY.session)),
      cart: asCart(parse(KEY.cart)),
      // The same reader: both maps are service key → a positive whole number,
      // and an entry that survived the write but not the parse should drop
      // from either of them the same way.
      cartHours: asCart(parse(KEY.cartHours)),
    };
  } catch {
    return fallback;
  }
}

export const saveOnboarded = (): void => write(KEY.onboarded, true);
/**
 * The header line and the pin under it, written together.
 *
 * One function rather than two so the pair cannot drift: a line saved without
 * its pin is exactly the state that made the coverage check silent, and it is
 * an easy one to reintroduce by calling only half of this.
 *
 * `undefined` clears the pin - what a reset does, and what an address picked
 * from a source with no coordinates leaves behind. Storing `null` rather than
 * removing the key keeps the write fire-and-forget like every other one here.
 */
export const saveAddress = (
  address: string,
  pin?: { lat: number; lng: number },
): void => {
  write(KEY.address, address);
  write(KEY.addressPin, pin ?? null);
};
export const saveSession = (session: Session): void =>
  write(KEY.session, session);
export const saveCart = (cart: Record<string, number>): void =>
  write(KEY.cart, cart);
export const saveCartHours = (hours: Record<string, number>): void =>
  write(KEY.cartHours, hours);
export const savePrivacy = (settings: PrivacySettings): void =>
  write(KEY.privacy, settings);
export const saveSettings = (settings: AppSettings): void =>
  write(KEY.settings, settings);

/**
 * The privacy switches, read on their own.
 *
 * Not part of `loadApp`: nothing before the account page needs them, and adding
 * a fifth key to the launch read would slow every cold start for one screen.
 * Each field is checked individually so a settings blob written by an older
 * build - one switch short - comes back with the new switch at its default
 * rather than as `undefined`.
 */
export async function loadPrivacy(): Promise<PrivacySettings> {
  const stored = await read<unknown>(KEY.privacy, null);
  if (typeof stored !== 'object' || stored === null) {
    return defaultPrivacy;
  }
  const raw = stored as Record<string, unknown>;
  const bool = (key: keyof PrivacySettings): boolean =>
    typeof raw[key] === 'boolean' ? (raw[key] as boolean) : defaultPrivacy[key];

  return {
    appLock: bool('appLock'),
    analytics: bool('analytics'),
    personalised: bool('personalised'),
    shareLive: bool('shareLive'),
  };
}

/**
 * The app preferences, read on their own.
 *
 * Same reasoning as `loadPrivacy`: not worth a sixth key in the launch read for
 * one screen. Each field is validated against the values this build actually
 * understands rather than trusted - a language code written by a later build,
 * or a `theme` of `"blue"`, comes back as the default instead of putting an
 * unrenderable value into state.
 */
export async function loadSettings(): Promise<AppSettings> {
  const stored = await read<unknown>(KEY.settings, null);
  if (typeof stored !== 'object' || stored === null) {
    return defaultSettings;
  }
  const raw = stored as Record<string, unknown>;

  const oneOf = <T extends string>(key: keyof AppSettings, allowed: readonly T[]): T =>
    allowed.includes(raw[key] as T)
      ? (raw[key] as T)
      : (defaultSettings[key] as T);

  const bool = (key: keyof AppSettings): boolean =>
    typeof raw[key] === 'boolean' ? (raw[key] as boolean) : (defaultSettings[key] as boolean);

  return {
    language: oneOf('language', ['en', 'hi', 'mr'] as const),
    currency: oneOf('currency', ['INR'] as const),
    theme: oneOf('theme', ['system', 'light', 'dark'] as const),
    sounds: bool('sounds'),
    dataSaver: bool('dataSaver'),
  };
}

/** Used by nothing yet; here so "clear my data" has one place to call. */
export async function clearAll(): Promise<void> {
  try {
    await AsyncStorage.removeMany(Object.values(KEY));
  } catch {
    // Nothing to do - the next read falls back to defaults either way.
  }
}

/** Exported for the tests, which assert against the real key names. */
/**
 * Addresses saved on this device because the platform would not take them.
 *
 * ---------------------------------------------------------------------------
 * WHY THESE LIVE HERE AND NOT ON THE SERVER
 * ---------------------------------------------------------------------------
 * `POST /customers/me/addresses` rejects a pin outside the served cities with
 * a 400. That is the correct answer for a booking and the wrong one for a
 * customer who has just typed their address and pressed Save: nothing is
 * wrong with the address, we simply have not arrived yet, and throwing it
 * away means they type it again on every launch to be told the same thing.
 *
 * So a refused address is kept here instead, and shown in the book marked
 * "Coming soon". It is a genuinely local record - no server id, not on the
 * customer's account, gone if they reinstall - and `localOnly` on the row is
 * what stops the rest of the app treating it as one the server knows.
 *
 * Stored under the whole `SavedAddress` shape rather than the server's row
 * shape, because there is no server row: this is the only representation
 * these ever have.
 *
 * NOT cleared on sign-out - see `clearAll`, which does clear it. These belong
 * to the device rather than the session, but sign-out is a "this is not my
 * phone any more" signal and leaving somebody's home address behind on it
 * would be worse than making them retype it.
 */
export const loadPendingAddresses = (): Promise<SavedAddress[]> =>
  read<SavedAddress[]>(KEY.pendingAddresses, []).then(found =>
    /* Defensive: a hand-edited or half-written file must not crash the book.
       Anything without a place and a pin cannot be shown or booked anyway. */
    Array.isArray(found)
      ? found.filter(item => !!item?.place?.id && !!item?.coords)
      : [],
  );

export const savePendingAddresses = (addresses: SavedAddress[]): void =>
  write(KEY.pendingAddresses, addresses);

export const storageKeys = KEY;

/* `read` is the single-key form of `loadApp`, kept for slices added later. */
export { read as readKey };
